'use client';
import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import ScrollContainer from 'react-indiana-drag-scroll';
import { useParams } from 'next/navigation';

import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import { callService } from '@api/remote-call';
import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import { FlexibleDiv } from '@components/formStyles';
import HeroCampaignBanner from '@components/ui/banners/hero-campaign-banner/HeroCampaignBanner';
import Markdown from '@components/utils/Markdown';
import { useDevice } from '@contexts/device';

import Banner from '../listing/components/Banner/Banner';
import {
  CampaignPromotionsWrapper,
  ContainerItem,
  ContentContainer,
  Layout,
  SearchContainer,
  BannerContainer,
  Divider,
  Container,
} from './HotelTicketStyle';
import HotelTicketSearchBar from './HotelTicketSearchBar';
import CategoryPromotionDesktop from '../../landing/components/category-promotion/CategoryPromotionDesktop';
import { CampaignPromotions } from '../../landing/components/campaign/CampaignPromotions/CampaignPromotions';
import CategoryPromotion from '../../landing/components/category-promotion/CategoryPromotion';
import { LandingBannerContainer } from '../../homepage/HomeStyle';
import {
  formatCategoryPromotion,
  formatToCampaignsBannerData,
} from '../../homepage/formats';
import ItemInfo from '../../flight/ticket/ItemInfo';
import ResponsiveListingBox from '../listing/utils/ResponsiveListingBox';

const breadCrumbItems = [
  { title: 'Ana Sayfa', url: '/' },
  { title: 'Yurt İçi Otelleri', url: '' },
];

const HotelTicket = () => {
  const {
    location,
    hotelRoomsList,
    startDate,
    endDate,
    setHotelDateSelection,
    isHotelDateSelected,
  } = useSearchBarContext();
  const [isShowModal, setIsShowModal] = useState(false);
  const { isDesktop } = useDevice();
  const params = useParams();

  const { data } = useQuery(
    ['hotel-landing', { ...params }],
    () =>
      callService(
        {
          service: 'ContentsService',
          method: 'getHotelLanding',
          version: 'v3',
        },
        {
          slug: params?.slug as string,
        }
      ),
    { enabled: Boolean(params?.slug) }
  );

  const bannerImages = useMemo(() => {
    const items: Array<{
      url?: string;
      alt?: string;
      href?: string;
    }> = [];

    data?.carousel?.map((item) =>
      item?.images?.map((image) =>
        items.push({
          url: image?.url,
          alt: image?.alt,
          href: item?.url,
        })
      )
    );
    return items;
  }, [data]);

  if (isDesktop === undefined) return null;
  return (
    <>
      <Layout>
        <SearchContainer>
          {data?.searchBar ? (
            <>
              {isDesktop ? (
                <ContainerItem marginTop={isDesktop ? 40 : 0}>
                  <HotelTicketSearchBar data={data?.searchBar?.[0]} />
                </ContainerItem>
              ) : (
                <ResponsiveListingBox
                  location={location}
                  startDate={startDate}
                  endDate={endDate}
                  hotelRoomsList={hotelRoomsList}
                  isModalVisible={isShowModal || isHotelDateSelected}
                  toggleIsModalVisible={() => setIsShowModal((val) => !val)}
                  hideModal={() => {
                    setIsShowModal(false);
                    setHotelDateSelection(false);
                  }}
                />
              )}
            </>
          ) : null}

          <ContainerItem marginTop={isDesktop ? 20 : 15}>
            <ContentContainer>
              <Breadcrumb items={breadCrumbItems} />
            </ContentContainer>
          </ContainerItem>
          {isDesktop ? <FlexibleDiv bottom={20} /> : null}
        </SearchContainer>

        <ContentContainer>
          {data?.description1 ? (
            <ContainerItem marginTop={20}>
              <ItemInfo
                title={data?.description1?.[0]?.title || ''}
                description=''
                hasTopMargin={false}
              />
              <Markdown text={data?.description1?.[0]?.text || ''} />
            </ContainerItem>
          ) : null}
        </ContentContainer>
      </Layout>

      {!isDesktop ? <Divider /> : null}

      <ContentContainer>
        <Layout>
          {data?.categoryPromotionItems &&
          data?.categoryPromotionItems?.length > 0 ? (
            <ContainerItem marginTop={isDesktop ? 20 : 8}>
              {isDesktop ? <FlexibleDiv bottom={13} /> : null}
              <ScrollContainer className='scroll-container'>
                <Container>
                  <CategoryPromotionDesktop
                    data={formatCategoryPromotion(
                      data?.categoryPromotionItems,
                      isDesktop
                    )}
                  />
                </Container>
              </ScrollContainer>
              {isDesktop ? <FlexibleDiv bottom={20} /> : null}
            </ContainerItem>
          ) : null}

          {data?.description2 ? (
            <ContainerItem marginTop={isDesktop ? 20 : 0}>
              <ItemInfo
                title={data?.description2?.[0]?.title || ''}
                description=''
                hasTopMargin={false}
              />

              <Markdown text={data?.description2?.[0]?.text || ''} />
              {isDesktop ? <FlexibleDiv bottom={20} /> : null}
            </ContainerItem>
          ) : null}

          {data?.campaignPromotion && data?.campaignPromotion?.length > 0 ? (
            <ContainerItem>
              <CampaignPromotionsWrapper>
                <ItemInfo
                  title={data?.campaignPromotion?.[0]?.title || ''}
                  description={data?.campaignPromotion?.[0]?.text || ''}
                  hasTopMargin={false}
                />
                {!isDesktop ? <FlexibleDiv bottom={-20} /> : null}

                {data?.campaignPromotion?.[0]?.items &&
                data?.campaignPromotion?.[0]?.link ? (
                  <CampaignPromotions
                    data={data?.campaignPromotion?.[0]?.items}
                    link={data?.campaignPromotion?.[0]?.link?.[0]}
                    showHeader={false}
                    linkPosition='flex-start'
                    imagesKey='homePageImages'
                  />
                ) : null}
              </CampaignPromotionsWrapper>
              {isDesktop ? <FlexibleDiv bottom={20} /> : null}
            </ContainerItem>
          ) : null}

          {data?.description3 ? (
            <ContainerItem marginTop={20}>
              <ItemInfo
                title={data?.description3?.[0]?.title || ''}
                description=''
                hasTopMargin={false}
              />

              <Markdown text={data?.description3?.[0]?.text || ''} />
            </ContainerItem>
          ) : null}

          {bannerImages?.length && isDesktop ? (
            <ContainerItem marginTop={20}>
              <BannerContainer>
                <Banner
                  images={bannerImages}
                  imagesWidth={1240}
                  imagesHeight={400}
                />
              </BannerContainer>
              <FlexibleDiv bottom={20} />
            </ContainerItem>
          ) : null}

          {data?.description4 ? (
            <ContainerItem marginTop={isDesktop ? 20 : 5}>
              <ItemInfo
                title={data?.description4?.[0]?.title || ''}
                description=''
                hasTopMargin={false}
              />

              <Markdown text={data?.description4?.[0]?.text || ''} />
              <FlexibleDiv bottom={20} />
            </ContainerItem>
          ) : null}

          {data?.categoryPromotion && data?.categoryPromotion.length > 0 ? (
            <ContainerItem marginTop={0}>
              <>
                <ItemInfo
                  title={data?.categoryPromotion?.[0]?.name || ''}
                  description=''
                  hasTopMargin={false}
                />
                <FlexibleDiv bottom={isDesktop ? -20 : -45} />
                <CategoryPromotion
                  items={data?.categoryPromotion?.[0]?.items}
                  layout={data?.categoryPromotion?.[0]?.layout}
                  link={data?.categoryPromotion?.[0]?.link}
                  categoryPromotionType='hotel'
                />
              </>
            </ContainerItem>
          ) : null}

          {data?.campaignBanner && data?.campaignBanner?.length > 0 ? (
            <ContainerItem marginTop={isDesktop ? 40 : 20}>
              <LandingBannerContainer>
                <HeroCampaignBanner
                  data={formatToCampaignsBannerData(
                    data?.campaignBanner?.[0],
                    isDesktop
                  )}
                />
              </LandingBannerContainer>
            </ContainerItem>
          ) : null}

          {data?.description5 ? (
            <ContainerItem marginTop={40}>
              <ItemInfo
                title={data?.description5?.[0]?.title || ''}
                description=''
                hasTopMargin={false}
              />

              <Markdown text={data?.description5?.[0]?.text || ''} />
              {isDesktop ? <FlexibleDiv bottom={20} /> : null}
            </ContainerItem>
          ) : null}
        </Layout>
      </ContentContainer>
    </>
  );
};

export default HotelTicket;
